import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Bell, BellOff, LogOut, Pin, Users } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import { ConfirmDialog } from "@altek/ui/components/feedback/ConfirmDialog";
import type { MenuAction } from "@altek/ui/components/ui/Menu";
import { toast } from "@altek/ui/lib/utils/toast";
import { chatApi } from "../api/chat.api";
import type { ChatConversationDetail } from "../api/chat.types";
import { GroupMembersDrawer, HandOverDialog } from "../components/GroupMembersDrawer";
import { canArchiveConversation, conversationTitle } from "../lib/chat-map";
import { chatErrorMessage } from "../lib/chat-errors";
import { invalidateAfterLeaving, invalidateChat } from "../lib/chat-invalidate";

/**
 * BİR KONUŞMAYA YAPILABİLECEKLER — sayfa ile yuvanın ORTAK tek kaynağı.
 *
 * NEDEN (denetim Sohbet 17). Sayfa ve yuva aynı konuşmanın iki yüzeyi, ama eylemleri iki ayrı
 * yerde yazılıydı ve ayrışmışlardı:
 * - yuvanın satır menüsündeki «Sessize al» sessize ALMIYORDU: yuvayı kapatıp sohbet sayfasına
 *   gidiyordu — kişi okuduğu ekrandan kopuyor, konuşma sessize alınmıyordu;
 * - yuvada arşivleme yoktu;
 * - «Gruptan ayrıl» tek dokunuşla gidiyordu: yanlışlıkla basan kişi gruptan düşüyor ve geri dönmek
 *   için birinin onu yeniden eklemesi gerekiyordu.
 * Eylem bir kez burada yazılınca iki yüzey aynı şeyi yapmak ZORUNDA.
 *
 * AYRILMAK ONAYLI. Kitin `ConfirmDialog`'u; cümle bir soru değil sonucun kendisi. İstek yalnız
 * onayla gidiyor; başarıda önce kutu kapanıyor, sonra listeler tazeleniyor (close-then-sync).
 *
 * HATALAR `chatErrorMessage` ile: «grubun sahibi olmalısınız», «destek oturumunda …» gibi
 * tasarlanmış retler kendi cümleleriyle söyleniyor.
 *
 * GRUP ÜYELERİ VE SAHİPLİK DEVRİ (sohbet-duzeltme 20 Ekim 2026, denetim H7). Grubun menüsünde
 * «Grup üyeleri» var: liste herkese, ekle/çıkar/ad/sahip yap sahibe (`GroupMembersDrawer`). TEK
 * SAHİP «Gruptan ayrıl»a basınca onay kutusu yerine devir penceresi açılıyor — sunucunun 422
 * `LAST_OWNER`'ı bir hata bildirimi olarak değil, yapılacak işin kendisi olarak.
 */

/**
 * Is the reader the group's ONLY owner, with somebody else still in it? Then leaving needs a
 * successor first — the server's last-owner rule, asked before the request (`assertCanLeave`).
 */
export function mustHandOver(conversation: ChatConversationDetail, meId: string): boolean {
  if (conversation.kind !== "GROUP" || conversation.role !== "OWNER") return false;
  const owners = conversation.allMembers.filter((member) => member.role === "OWNER");
  return owners.length <= 1 && owners.every((member) => member.userId === meId) && conversation.allMembers.length > 1;
}

/** «Sessize al» süresi: bir iş günü. Sunucu bir an saklıyor; istemci yalnız süre gönderiyor. */
export const MUTE_MINUTES = 480;

export interface ConversationActions {
  toggleMute: (conversation: { id: string; muted: boolean }) => void;
  toggleArchive: (conversation: { id: string; archived: boolean }) => void;
  /** Onay kutusunu açar; istek onaydan önce GİTMEZ. Tek sahip için devir penceresi açılır. */
  requestLeave: (conversation: ChatConversationDetail) => void;
  /** «Grup üyeleri» çekmecesini açar. */
  openMembers: (conversationId: string) => void;
  /** Konuşma başlığındaki menü: sabitle, sessize al, (yetkiliyse) arşivle, (grupsa) ayrıl. */
  menuFor: (
    conversation: ChatConversationDetail,
    pin: { pinned: boolean; toggle: () => void },
  ) => MenuAction[];
  /** Ayrılma onayı. Çağıranın ağacında bir kez çizilmeli. */
  dialog: ReactNode;
}

export function useConversationActions(options: {
  meId: string;
  /** Ayrılma başarılı olunca: sayfa listeye döner, yuva seçimi bırakır. */
  onLeft?: (conversationId: string) => void;
}): ConversationActions {
  const queryClient = useQueryClient();
  const { meId, onLeft } = options;
  const [leaving, setLeaving] = useState<ChatConversationDetail | null>(null);
  const [handingOver, setHandingOver] = useState<ChatConversationDetail | null>(null);
  const [membersOf, setMembersOf] = useState<string | null>(null);

  const mute = useMutation({
    mutationFn: (input: { id: string; untilMinutes: number | null }) =>
      chatApi.mute(input.id, input.untilMinutes),
    onSuccess: () => invalidateChat(queryClient),
    onError: (error) => toast.errorMessage("Sessize alma değiştirilemedi", chatErrorMessage(error)),
  });

  const archive = useMutation({
    mutationFn: (input: { id: string; archived: boolean }) =>
      input.archived ? chatApi.unarchive(input.id) : chatApi.archive(input.id),
    onSuccess: () => invalidateChat(queryClient),
    onError: (error, input) =>
      toast.errorMessage(
        input.archived ? "Sohbet arşivden çıkarılamadı" : "Sohbet arşivlenemedi",
        chatErrorMessage(error),
      ),
  });

  const leave = useMutation({
    mutationFn: (conversationId: string) => chatApi.removeMember(conversationId, meId),
    onSuccess: async (_result, conversationId) => {
      setLeaving(null);
      onLeft?.(conversationId);
      await invalidateAfterLeaving(queryClient);
      toast.success("Gruptan ayrıldınız.");
    },
    onError: (error) => toast.errorMessage("Gruptan ayrılınamadı", chatErrorMessage(error)),
  });

  const toggleMute = useCallback(
    (conversation: { id: string; muted: boolean }) =>
      mute.mutate({ id: conversation.id, untilMinutes: conversation.muted ? null : MUTE_MINUTES }),
    [mute],
  );

  const toggleArchive = useCallback(
    (conversation: { id: string; archived: boolean }) =>
      archive.mutate({ id: conversation.id, archived: conversation.archived }),
    [archive],
  );

  const requestLeave = useCallback(
    (conversation: ChatConversationDetail) => {
      if (mustHandOver(conversation, meId)) setHandingOver(conversation);
      else setLeaving(conversation);
    },
    [meId],
  );

  const openMembers = useCallback((conversationId: string) => setMembersOf(conversationId), []);

  const menuFor = useCallback(
    (
      conversation: ChatConversationDetail,
      pin: { pinned: boolean; toggle: () => void },
    ): MenuAction[] => [
      {
        label: pin.pinned ? "Sabitlemeyi kaldır" : "Sabitle",
        icon: <Pin size={16} strokeWidth={2} />,
        onSelect: pin.toggle,
      },
      {
        label: conversation.muted ? "Bildirimleri aç" : "Sessize al",
        icon: conversation.muted ? (
          <Bell size={16} strokeWidth={2} />
        ) : (
          <BellOff size={16} strokeWidth={2} />
        ),
        onSelect: () => toggleMute(conversation),
      },
      /**
       * ARCHIVING A GROUP IS THE OWNER'S, and the client HIDES what it may not do (§14b: "DM'de
       * iki taraf da arşivler; grupta sahip"). Offered to everyone it was a menu item whose only
       * effect was a 403 toast.
       */
      ...(canArchiveConversation(conversation)
        ? [
            {
              label: conversation.archived ? "Arşivden çıkar" : "Arşivle",
              icon: conversation.archived ? (
                <ArchiveRestore size={16} strokeWidth={2} />
              ) : (
                <Archive size={16} strokeWidth={2} />
              ),
              onSelect: () => toggleArchive(conversation),
            },
          ]
        : []),
      ...(conversation.kind === "GROUP"
        ? [
            {
              label: "Grup üyeleri",
              icon: <Users size={16} strokeWidth={2} />,
              onSelect: () => openMembers(conversation.id),
            },
            {
              label: "Gruptan ayrıl",
              icon: <LogOut size={16} strokeWidth={2} />,
              destructive: true,
              onSelect: () => requestLeave(conversation),
            },
          ]
        : []),
    ],
    [toggleMute, toggleArchive, requestLeave, openMembers],
  );

  const title = leaving ? (conversationTitle(leaving, meId) ?? "bu grup") : "";

  const dialog = (
    <>
      <ConfirmDialog
        open={leaving !== null}
        onOpenChange={(open) => {
          // Giden bir istek varken kutu kapanmaz: sonucu söyleyecek yer orası.
          if (!open && !leave.isPending) setLeaving(null);
        }}
        // Başlık bir soru, düğme eylemi tekrarlıyor (9 Ekim 2026, 5. tur — onay pencereleri taraması).
        title="Gruptan ayrılıyor musunuz?"
        impact={`«${title}» grubundan ayrılırsınız. Mesajlarını artık göremezsiniz; geri dönmek için gruba yeniden eklenmeniz gerekir.`}
        confirmLabel="Gruptan ayrıl"
        destructive
        isPending={leave.isPending}
        onConfirm={() => {
          if (leaving) leave.mutate(leaving.id);
        }}
      />
      <GroupMembersDrawer
        conversationId={membersOf}
        meId={meId}
        onClose={() => setMembersOf(null)}
        onRequestLeave={(conversation) => {
          setMembersOf(null);
          requestLeave(conversation);
        }}
      />
      <HandOverDialog
        conversation={handingOver}
        meId={meId}
        onClose={() => setHandingOver(null)}
        onLeft={(conversationId) => onLeft?.(conversationId)}
      />
    </>
  );

  return { toggleMute, toggleArchive, requestLeave, openMembers, menuFor, dialog };
}
